import { computed, ref } from "vue";
import { defineStore } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import { captureEvent } from "@/lib/analytics";
import { deepCopy } from "@utils/toolBox";
import type {
  AdTemplateDetail,
  AdTemplateHtml,
  AdTemplateSummary,
  AdTemplateVariables,
  AdTemplateVersion,
  AvailableFont,
} from "@/types";

export interface AdTemplateWorkingCopy {
  versionId: number | null;
  html_templates: AdTemplateHtml;
  m_template_attributes: AdTemplateVariables;
  message: string;
}

const copyOf = (version: AdTemplateVersion): AdTemplateWorkingCopy => ({
  versionId: version.status === "draft" ? version.id : null,
  html_templates: deepCopy(version.html_templates),
  m_template_attributes: deepCopy(version.m_template_attributes),
  message: version.status === "draft" ? version.message || "" : "",
});

const contentOf = (copy: Pick<AdTemplateWorkingCopy, "html_templates" | "m_template_attributes">) =>
  JSON.stringify({ html_templates: copy.html_templates, m_template_attributes: copy.m_template_attributes });

export const useAdTemplatesStore = defineStore("adTemplates", () => {
  const templates = ref<AdTemplateSummary[]>([]);
  const template = ref<AdTemplateDetail | null>(null);
  const fonts = ref<AvailableFont[]>([]);
  const working = ref<AdTemplateWorkingCopy | null>(null);
  const baseVersion = ref<AdTemplateVersion | null>(null);
  const errors = ref<string[]>([]);
  const backgrounds = ref<string[]>([]);
  const restoredFrom = ref<number | null>(null);

  const versions = computed(() => template.value?.versions ?? []);
  const latestDraft = computed(() => versions.value.find((version) => version.status === "draft") ?? null);
  const publishedVersion = computed(() => versions.value.find((version) => version.status === "published") ?? null);
  const workingDraft = computed(() => versions.value.find((version) => version.id === working.value?.versionId) ?? null);
  const hasUnsavedChanges = computed(
    () => !!working.value && !!baseVersion.value && contentOf(working.value) !== contentOf(baseVersion.value),
  );
  const canSave = computed(() => hasUnsavedChanges.value || (!!restoredFrom.value && !working.value?.versionId));

  const fetchTemplates = async () => {
    const { data } = await useMyFetch("/m_templates/editor").json<AdTemplateSummary[]>();
    templates.value = data.value ?? [];
  };

  const fetchFonts = async () => {
    const { data } = await useMyFetch("/m_templates/fonts").json<{ fonts: AvailableFont[] }>();
    fonts.value = data.value?.fonts ?? [];
  };

  const fetchBackgrounds = async () => {
    const { data } = await useMyFetch("/m_medium_uploads/search")
      .post({ per_page: 8 })
      .json<{ m_medium_uploads: Array<{ url: string }> }>();
    backgrounds.value = (data.value?.m_medium_uploads ?? []).map((upload) => upload.url);
  };

  const open = (version: AdTemplateVersion) => {
    baseVersion.value = version;
    working.value = copyOf(version);
    errors.value = [];
    restoredFrom.value = null;
  };

  const fetchTemplate = async (token: string) => {
    const { data, error } = await useMyFetch(`/m_templates/${token}`).json<AdTemplateDetail>();
    if (error.value) throw error.value;

    template.value = data.value;
    const version = versions.value.find((candidate) => candidate.status !== "archived") ?? versions.value[0];
    if (version) open(version);
    else {
      working.value = null;
      baseVersion.value = null;
    }
  };

  const startHtmlVersion = async () => {
    if (!template.value?.starter) return;
    const { starter } = template.value;
    const { data, error } = await useMyFetch(`/m_templates/${template.value.token}/versions`)
      .post({ ...starter, message: "Starter template" })
      .json<AdTemplateVersion>();
    if (error.value || !data.value) throw error.value;

    upsertVersion(data.value);
    open(data.value);
  };

  const createTemplate = async (name: string) => {
    const { data, error } = await useMyFetch("/m_templates").post({ name }).json<AdTemplateDetail>();
    if (error.value || !data.value) throw error.value;

    captureEvent("ad_template_created", { m_template_id: data.value.id });
    return data.value;
  };

  const setArchived = async (archive: boolean) => {
    if (!template.value) return;
    const { data, error } = await useMyFetch(`/m_templates/${template.value.token}/${archive ? "archive" : "unarchive"}`)
      .post()
      .json<{ archived_at: string | null }>();
    if (error.value || !data.value) throw error.value;

    template.value.archived_at = data.value.archived_at;
    if (archive) captureEvent("ad_template_archived", { m_template_id: template.value.id });
    else captureEvent("ad_template_unarchived", { m_template_id: template.value.id });
  };

  const upsertVersion = (version: AdTemplateVersion) => {
    if (!template.value) return;
    const others = template.value.versions.filter((other) => other.id !== version.id);
    template.value.versions = [version, ...others].sort((a, b) => b.number - a.number);
  };

  const saveDraft = async () => {
    if (!template.value || !working.value) return null;
    const { versionId, ...body } = working.value;
    const request = versionId
      ? useMyFetch(`/m_template_versions/${versionId}`).patch(body)
      : useMyFetch(`/m_templates/${template.value.token}/versions`).post(body);
    const { data, error, response } = await request.json<AdTemplateVersion>();

    if (response.value?.status === 422) {
      errors.value = [error.value ?? []].flat().map(String);
      return null;
    }
    if (error.value || !data.value) throw error.value;

    upsertVersion(data.value);
    open(data.value);
    captureEvent("ad_template_version_saved", { m_template_id: template.value.id, version_number: data.value.number });
    return data.value;
  };

  const publish = async (version: AdTemplateVersion) => {
    if (!template.value) return;
    const { data, error } = await useMyFetch(`/m_template_versions/${version.id}/publish`).post().json<AdTemplateVersion>();
    if (error.value || !data.value) throw error.value;

    captureEvent("ad_template_version_published", { m_template_id: template.value.id, version_number: data.value.number });
    await fetchTemplate(template.value.token);
  };

  const refreshDocumentUrl = async (versionId: number) => {
    const { data } = await useMyFetch(`/m_template_versions/${versionId}`).json<AdTemplateVersion>();
    const fresh = data.value;
    if (!fresh) return;
    const known = versions.value.find((version) => version.id === versionId);
    if (known) known.document_url = fresh.document_url;
    if (baseVersion.value?.id === versionId) baseVersion.value = { ...baseVersion.value, document_url: fresh.document_url };
  };

  const restore = (version: AdTemplateVersion) => {
    open(version);
    if (!working.value || version.status === "draft") return;
    working.value.message = `Restored from v${version.number}`;
    restoredFrom.value = version.number;
  };

  return {
    templates,
    template,
    fonts,
    working,
    baseVersion,
    errors,
    backgrounds,
    versions,
    latestDraft,
    publishedVersion,
    workingDraft,
    hasUnsavedChanges,
    canSave,
    fetchTemplates,
    fetchFonts,
    fetchBackgrounds,
    fetchTemplate,
    createTemplate,
    startHtmlVersion,
    saveDraft,
    publish,
    refreshDocumentUrl,
    restore,
    setArchived,
  };
});
